import { useEffect, useState, useContext } from "react";
import partCodesService from "services/part-code-service";
import changeLogService from "services/change-log-service";
import { AuthContext } from "context";
import formatPartCode from "helper/formatPartCode";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

// E4A React components
import DashboardLayout from "e4acomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "e4acomponents/Navbars/DashboardNavbar";
import Footer from "e4acomponents/Footer";
import { useNavigate, useParams } from "react-router-dom";

// Components
import AttributesFields from "../create-part-code/attributes-fields";
import GeneralInfoFields from "./general-info";
import EditImage from "./edit-image";
import EditFiles from "./edit-files";


const EditPart = () => {

  const { id } = useParams();
  const navigate = useNavigate();
  const [part, setPart] = useState({
    id: id,
    description: "N/A",
    unique_num: "",
    revision_num: 1,
    generatedUID: "",
    manufacturer: "",
    manufacturer_part_number: "",
    mpn: {},
    preferred_mpn: "",
    vendors: "",
    preferred_vendor: "",
    vendor_part_number: "",
    partCategory: {
      parentCategory: "",
      categoryCode: "",
      id: ""
    }
  });

  // Error flags for the input fields
  const [error, setError] = useState({});

  const [title, setTitle] = useState("Edit UID");
  const [image, setImage] = useState("");
  const [showAttributes, setShowAttributes] = useState(false);
  const [showFiles, setShowFiles] = useState(false);
  const [units, setUnits] = useState({});
  const [usedPartNumbers, setUsedPartNumbers] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [prevRev, setPrevRev] = useState(null);
  const authContext = useContext(AuthContext);

  // Fetches the parentCategories and partCategories for the options
  useEffect(() => {
    (async () => {
      setIsLoading(false);
      // Fetch the partcode to edit from the API
      const response = await partCodesService.getPartById(id);

      // Format the full part Code
      const fetchedPart = response.data.attributes;
      const generatedUid = formatPartCode(fetchedPart);

      // Initializing the part with the fetched data
      const defaultValues = {
        description: "N/A",
        manufacturer: "",
        manufacturer_part_number: "",
        mpn: {},
        preferred_mpn: "",
        vendors: "",
        preferred_vendor: "1",
        vendor_part_number: "",
        attributes: {},
        filePaths: {}
      };

      // Set the part state, merging default values with fetched values
      setPart({
        id,
        generatedUID: generatedUid,
        ...defaultValues,
        ...fetchedPart,
        attributes: fetchedPart.attributes || formatAttributes(fetchedPart.partCategory.attributes),
        partCategory: fetchedPart.partCategory
      });
      setPrevRev(fetchedPart.revision_num);
      // Set the page title
      setTitle('Edit ' + generatedUid.replace(/-/g, '–'));

      // If the part category has attributes, show the attributes fields
      if (fetchedPart.partCategory.has_attributes) {
        setUnits(formatUnits(fetchedPart.attributes, fetchedPart.partCategory.attributes));
        setShowAttributes(true);
      }

      // Show the files section once the part is fetched
      setShowFiles(true);

      // Set the image if it exists
      if (!fetchedPart.imagePath) { setImage("") } else { setImage(fetchedPart.imagePath) };

      // Fetch used part numbers to validate uniqueness
      const usedPartNumbersResponse = await partCodesService.getUsedPartNumbers();
      const usedPartNumbersList = usedPartNumbersResponse.data.attributes;

      // Filter out the current part's numbers from the lists
      let filteredResponse = { ...usedPartNumbersList };
      const manuIndex = filteredResponse.manufacturer_part_numbers.indexOf(fetchedPart.manufacturer_part_number);
      const vendorIndex = filteredResponse.vendor_part_numbers.indexOf(fetchedPart.vendor_part_number);

      if (manuIndex > -1) {
        filteredResponse.manufacturer_part_numbers.splice(manuIndex, 1);
      }
      if (vendorIndex > -1) {
        filteredResponse.vendor_part_numbers.splice(vendorIndex, 1);
      }

      // Set the used part numbers state
      setUsedPartNumbers(filteredResponse);

    })();
  }, []);

  useEffect(() => {
    const generatedUid = formatPartCode(part);
    changeHandler({ target: { name: "generatedUID", value: generatedUid } });
  }, [part.unique_num, part.revision_num]);

  // Generates the description of the partcode based on the entered attributes
  // Effect hook to update the description whenever part attributes or units change

  useEffect(() => {
    // Check if part.attributes exists, is an object, and is not empty
    if (part.attributes && typeof part.attributes === 'object' && Object.keys(part.attributes).length > 0) {
      // Initialize description with category code
      let description = part.partCategory.categoryCode + " ";

      // Get category attributes or empty object if none exist (clone to avoid pass-by-reference)
      let categoryAttributes = part.partCategory.attributes
        ? (Array.isArray(part.partCategory.attributes)
          ? [...part.partCategory.attributes]
          : { ...part.partCategory.attributes })
        : {};

      if (categoryAttributes[0]) {
        let keysToDisplay = [];
        // Handle conditional attributes if enabled
        if (categoryAttributes[0].enableCondAttrs) {
          // Verify that the value for the first attribute is a valid option
          const firstAttributeLabel = categoryAttributes[0].attribute;
          const firstAttributeValue = part.attributes[firstAttributeLabel];
          const validOptions = categoryAttributes[0].options.split(";");

          if (!firstAttributeValue || !validOptions.includes(firstAttributeValue)) {
            return;
          }

          const type = part.attributes[categoryAttributes[0].attribute];
          keysToDisplay = categoryAttributes[0][type].split(";");
        } else {
          // Otherwise use all category attributes
          keysToDisplay = Object.keys(categoryAttributes);
        }

        // Remove attributes that shouldn't be displayed
        for (const key in categoryAttributes) {
          if (!keysToDisplay.includes(key)) {
            delete categoryAttributes[key];
          }
        }
      }

      // Build description string from attributes
      for (const key in categoryAttributes) {
        const label = categoryAttributes[key].attribute;
        const value = part.attributes ? part.attributes[label] : '';

        // Handle different value types (with units, arrays, or plain values)
        if (units[label] && value) {
          description += value + units[label] + " ";
        } else if (Array.isArray(value)) {
          description += value.join(', ');
          description += ' ';
        }
        else if (value) {
          description += value;
          description += ' ';
        }
      }

      // Update part state with new description, removing trailing comma if present
      setPart(prev => ({
        ...prev,
        description: description.trim()[description.length - 1] === ',' ? description.slice(0, -1) : description
      }));
    }
  }, [part.attributes, units]);

  // Formats fetched part's attributes into usable object
  const formatAttributes = (attributes) => {
    const newAtt = {};
    for (const key in attributes) {
      newAtt[key] = attributes[key];
    }
    return newAtt;
  }

  // Formats fetched part's attributes into usable units array
  const formatUnits = (partAttributes, categoryAttributes) => {
    const newUnits = {};

    for (const key in categoryAttributes) {
      const attribute = categoryAttributes[key];

      if (attribute.fieldType === "text-with-units" && partAttributes[attribute.attribute]) {
        const attributeValue = partAttributes[attribute.attribute];
        const valueParts = attributeValue ? attributeValue.split(';') : [];

        if (valueParts.length > 1) {
          newUnits[attribute.attribute] = valueParts[1];
          changeHandler({ target: { name: `attributes.${attribute.attribute}`, value: valueParts[0] } });
        } else {
          newUnits[attribute.attribute] = '';
          changeHandler({ target: { name: `attributes.${attribute.attribute}`, value: attributeValue } });
        }
      }
    }
    return newUnits;
  }

  // Concatenates attributes and their corresponding units (seperated by semicolon)
  const concatUnits = () => {
    let attributes = { ...part.attributes };
    Object.keys(units).forEach(key => {
      attributes[key] = attributes[key] + ";" + units[key];
    });
    return attributes;
  }

  //Handles the changes of the input fields
  const changeHandler = (e) => {
    const { name, value } = e.target;

    if (name === "revision_num") {
      const numericValue = Number(value);
      if (prevRev !== null && numericValue < prevRev) {
        return;
      }
      setPart(prev => ({
        ...prev,
        revision_num: numericValue
      }));
      return;
    }

    const [prefix, field] = name.split('.');
    if (field) {
      setPart(prev => ({
        ...prev,
        [prefix]: {
          ...prev[prefix],
          [field]: value
        }
      }));
    } else {
      setPart(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handles the changes of the units selectors
  const unitsChangeHandler = (event) => {
    setUnits({ ...units, [event.target.name]: event.target.value });
  };

  // Checks if any field is empty and raises its corresponding error flag
  const fieldsValidator = () => {
    const newErrors = {};

    let hasErrors = false;

    const requiredFields = [
      ['partCategory.parentCategory', 'parentCategory'],
      ['partCategory.categoryCode', 'categoryCode'],
      ['description', 'description'],
      ['manufacturer', 'manufacturer'],
      ['vendor_part_number', 'vendor_part_number']
    ];
    const fieldFullNames = {
      parentCategory: 'Parent Category',
      categoryCode: 'Category Code',
      description: 'Description',
      manufacturer: 'Manufacturer',
      vendor1: 'Vendor 1',
      vendor2: 'Vendor 2',
      vendor_part_number: 'Vendor Part Number',
    }

    // Validate required fields
    for (const [path, errorKey] of requiredFields) {
      const value = path.includes('.')
        ? part[path.split('.')[0]][path.split('.')[1]]
        : part[path];

      if (!value?.trim()) {
        newErrors[errorKey] = `The ${fieldFullNames[errorKey]} field is required`;
        hasErrors = true;
      }
    }

    // Validate MPN dictionary
    const mpnValues = Object.values(part.mpn || {});
    if (mpnValues.length === 0) {
      newErrors.mpn = "At least one MPN is required";
    } else {
      const invalidMpnKeys = [];
      Object.entries(part.mpn).forEach(([key, mpn]) => {
        if (!mpn?.trim()) {
          invalidMpnKeys.push(key);
        }
      });
      if (invalidMpnKeys.length > 0) {
        newErrors.mpn = `MPN entries at keys ${invalidMpnKeys.join(', ')} cannot be empty`;
      }
    }

    // Validate preferred_mpn
    if (Object.keys(part.mpn || {}).length > 0) {
      if (!part.preferred_mpn || !part.preferred_mpn.toString().trim()) {
        newErrors.preferred_mpn = "The Preferred MPN field is required";
      } else if (!Object.keys(part.mpn).includes(part.preferred_mpn.toString())) {
        newErrors.preferred_mpn = "The Preferred MPN must be one of the provided MPN keys";
      }
    }

    // Vendor validation
    const hasVendor1 = part.vendors?.['vendor-1']?.trim();
    const hasVendor2 = part.vendors?.['vendor-2']?.trim();
    const hasLink1 = part.vendors?.['link-1']?.trim();
    const hasLink2 = part.vendors?.['link-2']?.trim();
    if (part.preferred_vendor === "1") {
      if (!hasVendor1) {
        newErrors.vendor1 = "Vendor 1 is required when selected as preferred vendor";
        hasErrors = true;
      } if (!hasLink1) {
        newErrors.link1 = "Link 1 is required when vendor 1 is preferred vendor";
        hasErrors = true;
      }
    }
    if (part.preferred_vendor === "2") {
      if (!hasVendor2) {
        newErrors.vendor2 = "Vendor 2 is required when selected as preferred vendor";
        hasErrors = true;
      } if (!hasLink2) {
        newErrors.link2 = "Link 2 is required when vendor 2 is preferred vendor";
        hasErrors = true;
      }
    }

    if (image === "" &&
      part.partCategory.parentCategory &&
      !["SOFTWARE", "DOCUMENTS AND FILES"].includes(part.partCategory.parentCategory)) {
      newErrors.image = "An image is required for this part category";
      hasErrors = true;
    }

    return newErrors;
  }
  const validatePartNumbers = (fieldErrors) => {
    // If there are already errors on the part numbers, skip validation
    if (fieldErrors.manufacturer_part_number || fieldErrors.vendor_part_number) {
      return false;
    }

    let partnumErrors = {};
    const usedManuPartNums = usedPartNumbers.manufacturer_part_numbers || [];
    const usedVendorPartNums = usedPartNumbers.vendor_part_numbers || [];

    // Check if the entered part numbers are in the used part numbers lists
    if (usedManuPartNums.includes(part.manufacturer_part_number)) {
      partnumErrors.manufacturer_part_number = "This Manufacturer Part Number is already used by another part";
    } if (usedVendorPartNums.includes(part.vendor_part_number)) {
      partnumErrors.vendor_part_number = "This Vendor Part Number is already used by another part";
    }
    return partnumErrors;
  }

  // Validates the attributes based on the part category's conditional rules (if they exist)
  const validateAttributes = () => {
    if (!part.partCategory.has_attributes) return {};
    let attributeErrors = {};
    const categoryAttributes = part.partCategory.attributes;

    // Conditional Attribute Rules validation
    if (categoryAttributes[0]?.enableCondAttrs) {
      const attKey = categoryAttributes[0]?.attribute;

      if (part.attributes[attKey]) {
        const type = part.attributes[attKey];
        const rules = categoryAttributes[0][type].split(";");

        for (const key in categoryAttributes) {
          const label = categoryAttributes[key].attribute;
          const unitSpec = categoryAttributes[key]?.units;
          const unitVal = units[label];

          if (rules.includes(label) && !part.attributes[label]) {
            attributeErrors[label] = `The ${label} field is required when ${attKey} is set to ${type}`;
          }
          if (categoryAttributes[key].fieldType !== "text-with-units" && String(part.attributes[label]).includes(';')) {
            attributeErrors[label] = `The ${label} field should not contain semicolons`;
          }
          if (rules.includes(key) && unitSpec) {
            if (
              unitVal === undefined ||
              unitVal === null ||
              (typeof unitVal === "string" && unitVal.trim() === "" && unitVal !== " ")
            ) {
              attributeErrors[label] = `The unit for ${label} field is required.`;
            }
          }
        }
      }
    } else {
      for (const key in categoryAttributes) {
        const label = categoryAttributes[key].attribute;
        const unitSpec = categoryAttributes[key]?.units;
        const unitVal = units[label];

        if (!part.attributes[label]) {
          attributeErrors[label] = `The ${label} field is required`;
        }
        if (categoryAttributes[key].fieldType !== "text-with-units" && String(part.attributes[label]).includes(';')) {
          attributeErrors[label] = `The ${label} field should not contain semicolons`;
        }
        if (unitSpec) {
          if (
            unitVal === undefined ||
            unitVal === null ||
            (typeof unitVal === "string" && unitVal.trim() === "" && unitVal !== " ")
          ) {
            attributeErrors[label] = `The unit for ${label} field is required.`;
          }
        }
      }
    }
    return attributeErrors;
  }

  //Submit handler
  const submitHandler = async (e) => {

    e.preventDefault();

    const fieldErrors = fieldsValidator();
    const partnumErrors = validatePartNumbers(fieldErrors);
    const attributeErrors = validateAttributes();
    const allErrors = {
      ...fieldErrors,
      ...partnumErrors,
      ...attributeErrors
    };

    setError(allErrors);
    if (Object.values(allErrors).some(err => err) || (allErrors.attributes && Object.values(allErrors.attributes).some(err => err))) {
      return;
    }

    setIsLoading(true);

    const attributes = concatUnits(part.attributes, units);

    const UpdatePart = {
      data: {
        type: "part",
        attributes: {
          ...part,
          attributes,
          partCategory: part.partCategory.id,
          status: "Pending Approval"
        },
        logs: {
          user: await authContext.getCurrentUser(),
        }
      }
    };

    try {
      console.log("Updating part with data:");
      await partCodesService.editPart(UpdatePart, id);
      console.log("Part updated successfully.");
      navigate(-1);
      console.log("Navigated back to previous page.");
      setIsLoading(false);

    } catch (err) {
      console.log('The error is here', err);
      return null;
    }
  };

  // Transition for when the attribute fields are displayed
  const attributeFieldsTransition = {
    transition: 'opacity 0.3s ease-in-out, height 0.3s ease-in-out, margin 0.3s ease-in-out',
    opacity: showAttributes ? 1 : 0,
    height: showAttributes ? 'auto' : '0px',
    marginTop: showAttributes ? '16px' : '0px',
    overflow: 'hidden'
  }
  return (
    <DashboardLayout>
      <DashboardNavbar breadcrumbTitle={title.substring(5)} />
      <MDBox mt={5} mb={9}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={8}>

            {/* Title */}
            <MDBox mt={6} mb={8} textAlign="center">
              <MDBox mb={1}>
                <MDTypography variant="h3" fontWeight="bold">
                  {title}
                </MDTypography>
              </MDBox>
              <MDTypography variant="h5" fontWeight="regular" color="secondary">
                Please do not forget to save your changes by clicking the "Edit" button
              </MDTypography>
            </MDBox>

            {/* Body (Card) */}
            <Card>
              <MDBox
                component="form"
                method="POST"
                onSubmit={submitHandler}
                encType="multipart/form-data"
              >
                <MDBox display="flex" flexDirection="column" px={3} my={4}>
                  {isLoading ? (
                    <MDBox display="flex" justifyContent="center" alignItems="center" height="84vh">
                      <CircularProgress />
                    </MDBox>
                  ) : (
                    <MDBox>
                      {/* Edit Image section */}
                      <EditImage
                        part={part}
                        setPart={setPart}
                        image={image}
                        setImage={setImage}
                      />

                      {/* General Info Fields*/}
                      <GeneralInfoFields
                        part={part}
                        setPart={setPart}
                        changeHandler={changeHandler}
                        error={error}
                      />

                      {/* Attributes Fields */}
                      <MDBox sx={attributeFieldsTransition}>
                        {showAttributes && (
                          <>
                            <AttributesFields
                              part={part}
                              setPart={setPart}
                              changeHandler={changeHandler}
                              error={error}
                              units={units}
                              unitsChangeHandler={unitsChangeHandler} />
                          </>
                        )}
                      </MDBox>

                      {/* Edit Files section */}
                      {showFiles && (<EditFiles part={part} setPart={setPart} />)}
                    </MDBox>
                  )}
                  {/* Buttons */}
                  <MDBox ml="auto" mt={4} mb={2} display="flex" justifyContent="flex-end">

                    <MDBox mx={1}>
                      <MDButton
                        variant="gradient"
                        color="dark"
                        size="small"
                        px={2}
                        mx={2}
                        onClick={() =>
                          navigate(-1)
                        }
                      >
                        Cancel
                      </MDButton>
                    </MDBox>
                    <MDBox mx={1}>
                      <MDButton variant="gradient" color="dark" size="small" type="submit">
                        Save
                      </MDButton>
                    </MDBox>

                  </MDBox>


                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  )
};
export default EditPart;